/**
 * 📡 DrawRealtimeManager — Merkezi Çekiliş Realtime Orkestratörü
 *
 * Tek sorumlu singleton: 3 kanal, 5 güvenlik katmanı
 * ├── GroupSync    → postgres_changes (groups UPDATE)
 * ├── DrawLive     → broadcast (draw_starting, turn_revealed, draw_complete)
 * └── Presence     → presence (online users, spectators)
 *
 * Güvenlik Katmanları:
 * 1. Connection Monitor  → WebSocket sağlık kontrolü
 * 2. Auto-Reconnect      → Kademeli geri bağlanma
 * 3. Polling Guard       → Realtime başarısızsa DB fallback
 * 4. Event Dedup         → Mükerrer broadcast engeli
 * 5. Notification Sync   → Postacı bildirim senkronizasyon (ceremonyActive flag)
 */

import { supabase } from '@/lib/supabase'
import { RealtimeChannel } from '@supabase/supabase-js'
import { ConnectionMonitor } from './connectionMonitor'
import { realtimeLogger } from './logger'
import { ConnectionState, TurnRevealedEvent, DrawCompleteEvent, GroupSyncPayload, SpectatorInfo, PresenceState, DrawRealtimeCallbacks, POLLING_INTERVAL } from './types'

type Listener = () => void

class DrawRealtimeManager {
  // ─── Core State ───
  private activeGroupId: string | null = null
  private connectionState: ConnectionState = 'idle'
  private ceremonyActive = false

  // ─── Channels ───
  private groupSyncChannel: RealtimeChannel | null = null
  private drawLiveChannel: RealtimeChannel | null = null
  private presenceChannel: RealtimeChannel | null = null

  // ─── Monitors ───
  private connectionMonitor: ConnectionMonitor
  private groupPollingTimer: ReturnType<typeof setInterval> | null = null
  private drawPollingTimer: ReturnType<typeof setInterval> | null = null

  // ─── Event Dedup ───
  private processedEventIds = new Set<string>()

  // ─── Presence ───
  private presenceState: PresenceState = { spectatorCount: 0, spectators: [] }

  // ─── Callbacks ───
  private callbacks: Partial<DrawRealtimeCallbacks> = {}

  // ─── Listeners (React state sync) ───
  private listeners = new Set<Listener>()

  // ─── User Info (for Presence) ───
  private userId: string | null = null
  private userDisplayName: string | null = null
  private userAvatarUrl: string | null = null

  constructor() {
    this.connectionMonitor = new ConnectionMonitor(
      (state) => this.onConnectionStateChange(state),
      () => this.reconnect()
    )
  }

  // ═══════════════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════════════

  /**
   * Pilot Checklist — 6 adımlı bağlantı süreci
   */
  async connect(
    groupId: string,
    callbacks: Partial<DrawRealtimeCallbacks>,
    userInfo?: { userId: string; displayName: string; avatarUrl: string | null }
  ) {
    // ADIM 1: Mevcut bağlantıyı temizle
    if (this.activeGroupId === groupId && this.connectionState === 'connected') {
      // Zaten bağlı, callback'leri güncelle
      this.callbacks = callbacks
      return
    }

    if (this.activeGroupId && this.activeGroupId !== groupId) {
      this.disconnect()
    }

    this.activeGroupId = groupId
    this.callbacks = callbacks
    this.setConnectionState('connecting')
    realtimeLogger.connecting(groupId)

    if (userInfo) {
      this.userId = userInfo.userId
      this.userDisplayName = userInfo.displayName
      this.userAvatarUrl = userInfo.avatarUrl
    }

    try {
      // ADIM 2: GroupSync kanalını aç
      this.setupGroupSyncChannel(groupId)

      // ADIM 3: DrawLive kanalını aç
      this.setupDrawLiveChannel(groupId)

      // ADIM 4: Presence kanalını aç
      this.setupPresenceChannel(groupId)

      // ADIM 5: Connection Monitor başlat
      this.connectionMonitor.startHeartbeat()

      // ADIM 6: Polling Guard başlat (gerekirse)
      // Polling, dışarıdan startGroupPolling / startDrawPolling ile tetiklenir

    } catch (error) {
      realtimeLogger.failed(groupId, String(error))
      this.setConnectionState('failed')
    }
  }

  /**
   * Temizlik Checklist — 5 adımlı bağlantı kapatma
   */
  disconnect() {
    const groupId = this.activeGroupId || 'unknown'

    // ADIM 1: Polling durdur
    this.stopGroupPolling()
    this.stopDrawPolling()

    // ADIM 2: Presence'dan ayrıl
    if (this.presenceChannel) {
      try { this.presenceChannel.untrack() } catch { /* sessiz */ }
    }

    // ADIM 3: Tüm kanalları kapat
    if (this.groupSyncChannel) {
      supabase.removeChannel(this.groupSyncChannel)
      this.groupSyncChannel = null
    }
    if (this.drawLiveChannel) {
      supabase.removeChannel(this.drawLiveChannel)
      this.drawLiveChannel = null
    }
    if (this.presenceChannel) {
      supabase.removeChannel(this.presenceChannel)
      this.presenceChannel = null
    }

    // ADIM 4: ceremonyActive sıfırla
    this.ceremonyActive = false

    // ADIM 5: State temizle
    this.connectionMonitor.reset()
    this.activeGroupId = null
    this.processedEventIds.clear()
    this.presenceState = { spectatorCount: 0, spectators: [] }
    this.setConnectionState('idle')
    this.notifyListeners()

    realtimeLogger.disconnected(groupId)
  }

  // ─── Ceremony State (Spoiler önleme için) ───

  getCeremonyActive(): boolean { return this.ceremonyActive }

  setCeremonyActive(active: boolean) {
    this.ceremonyActive = active
    this.notifyListeners()
  }

  // ─── Getters ───

  getConnectionState(): ConnectionState { return this.connectionState }
  getPresenceState(): PresenceState { return this.presenceState }
  getActiveGroupId(): string | null { return this.activeGroupId }

  // ─── React Subscription (useSyncExternalStore pattern) ───

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getSnapshot(): {
    connectionState: ConnectionState
    ceremonyActive: boolean
    presence: PresenceState
    groupId: string | null
  } {
    return {
      connectionState: this.connectionState,
      ceremonyActive: this.ceremonyActive,
      presence: this.presenceState,
      groupId: this.activeGroupId,
    }
  }

  // ─── Polling Controls ───

  startGroupPolling(fetchGroupFn: () => Promise<GroupSyncPayload | null>) {
    this.stopGroupPolling()
    realtimeLogger.pollingStarted('GroupSync fallback')
    this.groupPollingTimer = setInterval(async () => {
      const data = await fetchGroupFn()
      if (data) {
        realtimeLogger.pollingResult(true, { draw_scheduled_at: data.draw_scheduled_at })
        this.callbacks.onGroupUpdate?.(data)
        // draw_scheduled_at set olunca polling durdur
        if (data.draw_scheduled_at) {
          this.stopGroupPolling()
        }
      }
    }, POLLING_INTERVAL)
  }

  stopGroupPolling() {
    if (this.groupPollingTimer) {
      clearInterval(this.groupPollingTimer)
      this.groupPollingTimer = null
      realtimeLogger.pollingStopped()
    }
  }

  startDrawPolling(fetchResultsFn: () => Promise<boolean>) {
    this.stopDrawPolling()
    realtimeLogger.pollingStarted('DrawLive fallback')
    this.drawPollingTimer = setInterval(async () => {
      const hasResults = await fetchResultsFn()
      if (hasResults) {
        realtimeLogger.pollingResult(true, { resultsFound: true })
        this.stopDrawPolling()
      }
    }, POLLING_INTERVAL)
  }

  stopDrawPolling() {
    if (this.drawPollingTimer) {
      clearInterval(this.drawPollingTimer)
      this.drawPollingTimer = null
    }
  }

  // ═══════════════════════════════════════════════════
  // PRIVATE — Channel Setup
  // ═══════════════════════════════════════════════════

  private setupGroupSyncChannel(groupId: string) {
    this.groupSyncChannel = supabase
      .channel(`gv:group:${groupId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'groups',
        filter: `id=eq.${groupId}`,
      }, (payload) => {
        const updated = payload.new as GroupSyncPayload

        // Değişen alanları tespit et ve logla
        if (updated.draw_scheduled_at !== undefined) {
          realtimeLogger.groupUpdated('draw_scheduled_at', updated.draw_scheduled_at)
        }
        if (updated.status !== undefined) {
          realtimeLogger.groupUpdated('status', updated.status)
        }
        if (updated.is_locked !== undefined) {
          realtimeLogger.groupUpdated('is_locked', updated.is_locked)
        }

        this.callbacks.onGroupUpdate?.(updated)
      })
      .subscribe((status) => {
        this.connectionMonitor.handleSubscriptionStatus('GroupSync', status)
      })

    this.connectionMonitor.registerChannel('GroupSync', this.groupSyncChannel)
  }

  private setupDrawLiveChannel(groupId: string) {
    this.drawLiveChannel = supabase
      .channel(`gv:draw:${groupId}`)
      .on('broadcast', { event: 'draw_starting' }, (_payload) => {
        realtimeLogger.drawStarting(groupId)
        this.ceremonyActive = true
        this.notifyListeners()
        this.callbacks.onDrawStarting?.()
      })
      .on('broadcast', { event: 'turn_revealed' }, (payload) => {
        const data = payload.payload as TurnRevealedEvent['payload']

        // Event dedup
        const eventKey = `turn:${groupId}:${data.turnIndex}`
        if (this.processedEventIds.has(eventKey)) return
        this.processedEventIds.add(eventKey)

        realtimeLogger.turnRevealed(data.turnIndex, data.winnerId)
        this.callbacks.onTurnRevealed?.(data)
      })
      .on('broadcast', { event: 'draw_complete' }, (payload) => {
        const data = payload.payload as DrawCompleteEvent['payload']

        // Event dedup
        const eventKey = `complete:${groupId}`
        if (this.processedEventIds.has(eventKey)) return
        this.processedEventIds.add(eventKey)

        realtimeLogger.drawComplete(groupId, data.totalTurns)
        // ceremonyActive, animasyon tamamlanınca dışarıdan false yapılır
        this.callbacks.onDrawComplete?.(data)
      })
      .subscribe((status) => {
        this.connectionMonitor.handleSubscriptionStatus('DrawLive', status)
      })

    this.connectionMonitor.registerChannel('DrawLive', this.drawLiveChannel)
  }

  private setupPresenceChannel(groupId: string) {
    this.presenceChannel = supabase
      .channel(`gv:presence:${groupId}`)
      .on('presence', { event: 'sync' }, () => {
        const state = this.presenceChannel?.presenceState() ?? {}
        const spectators: SpectatorInfo[] = []

        for (const key of Object.keys(state)) {
          const presences = state[key] as any[]
          for (const p of presences) {
            spectators.push({
              userId: p.userId || key,
              displayName: p.displayName || 'Anonim',
              avatarUrl: p.avatarUrl || null,
              joinedAt: p.joinedAt || new Date().toISOString(),
            })
          }
        }

        this.presenceState = {
          spectatorCount: spectators.length,
          spectators,
        }
        realtimeLogger.presenceSync(spectators.length)
        this.callbacks.onPresenceSync?.(this.presenceState)
        this.notifyListeners()
      })
      .on('presence', { event: 'join' }, ({ newPresences }) => {
        for (const p of newPresences) {
          realtimeLogger.presenceJoin((p as any).userId || 'unknown')
        }
      })
      .on('presence', { event: 'leave' }, ({ leftPresences }) => {
        for (const p of leftPresences) {
          realtimeLogger.presenceLeave((p as any).userId || 'unknown')
        }
      })
      .subscribe(async (status) => {
        this.connectionMonitor.handleSubscriptionStatus('Presence', status)
        if (status === 'SUBSCRIBED' && this.userId) {
          await this.presenceChannel?.track({
            userId: this.userId,
            displayName: this.userDisplayName,
            avatarUrl: this.userAvatarUrl,
            joinedAt: new Date().toISOString(),
          })
        }
      })

    this.connectionMonitor.registerChannel('Presence', this.presenceChannel)
  }

  // ═══════════════════════════════════════════════════
  // PRIVATE — Connection Management
  // ═══════════════════════════════════════════════════

  private onConnectionStateChange(state: ConnectionState) {
    this.setConnectionState(state)
    if (state === 'degraded' && this.activeGroupId) {
      realtimeLogger.degraded(this.activeGroupId)
    }
  }

  private setConnectionState(state: ConnectionState) {
    if (this.connectionState === state) return
    this.connectionState = state
    this.callbacks.onConnectionStateChange?.(state)
    this.notifyListeners()
  }

  private async reconnect(): Promise<boolean> {
    if (!this.activeGroupId) return false

    const groupId = this.activeGroupId

    // Eski kanalları temizle (disconnect çağırmadan — state'i koruyarak)
    if (this.groupSyncChannel) supabase.removeChannel(this.groupSyncChannel)
    if (this.drawLiveChannel) supabase.removeChannel(this.drawLiveChannel)
    if (this.presenceChannel) supabase.removeChannel(this.presenceChannel)
    this.connectionMonitor.clearChannels()

    try {
      this.setupGroupSyncChannel(groupId)
      this.setupDrawLiveChannel(groupId)
      this.setupPresenceChannel(groupId)
      realtimeLogger.reconnected(groupId)
      return true
    } catch {
      return false
    }
  }

  private notifyListeners() {
    for (const listener of this.listeners) {
      listener()
    }
  }
}

// Singleton export
export const drawRealtimeManager = new DrawRealtimeManager()
